import { z } from 'zod';

/**
 * The 6 noyaux — GSP/GPS/PGS/PSG/SPG/SGP. Each bridges to one of the PMS
 * Design System's brand colors via `NOYAU_COLOR_TOKEN` (see
 * packages/frontend/src/styles.css `@theme`) — the color tokens themselves
 * keep their existing names; only the business-facing code changed.
 */
export const noyauSchema = z.enum(['GSP', 'GPS', 'PGS', 'PSG', 'SPG', 'SGP']);
export type Noyau = z.infer<typeof noyauSchema>;

/** Picker display order and hover-tooltip order. */
export const NOYAU_ORDER: readonly Noyau[] = ['GSP', 'GPS', 'PGS', 'PSG', 'SPG', 'SGP'];

export type NoyauColorToken = 'bleu-nuit' | 'bleu-ciel' | 'vert' | 'jaune' | 'rouge' | 'rose';

/** Bridges a noyau code to its (unchanged) PMS color token / Tailwind class fragment. */
export const NOYAU_COLOR_TOKEN: Record<Noyau, NoyauColorToken> = {
  GSP: 'rose',
  GPS: 'rouge',
  PGS: 'jaune',
  PSG: 'vert',
  SPG: 'bleu-ciel',
  SGP: 'bleu-nuit',
};

// ── The `noyau-gradient` header ramp ─────────────────────────────────────────
//
// Shared because two renderers draw it from the same maths: the matrix grid
// (which speaks in `var(--color-…)` and lets the browser run `color-mix`) and
// the export (which needs literal hex — Playwright renders on `about:blank`
// and never loads `styles.css`, and the HTML export is a self-contained file).
//
// Only the *ramp* lives here, never the colour syntax. Each renderer turns a
// step into its own vocabulary. That is deliberate: issue #8 was caused by two
// surfaces holding private copies of one shared vocabulary and drifting apart.

/** Palier 6 is the ramp's end; step `k` mixes `k / PALIER_RAMP_MAX` of the noyau colour into white. */
export const PALIER_RAMP_MAX = 6;

/** The palier that is off the ramp entirely rather than at its bottom. */
export const SABOTAGE_PALIER = -10;

/**
 * Base colour per noyau token, as a literal hex and as the custom property it
 * mirrors in `styles.css`. `palier-palette-drift.test.ts` parses the real
 * stylesheet and asserts the two agree, so retuning a brand colour without
 * updating this map fails CI instead of shipping an export whose headers no
 * longer match the grid.
 *
 * `bleu-nuit` deliberately reads `--color-bleu-nuit-true`, not
 * `--color-bleu-nuit`: `NoyauAccentScope` shadows the latter for its whole
 * subtree to implement the `matrix-noyaux-accent` preview. Reading the shadowed
 * variable would let that preview silently recolour the gradient **only for SGP
 * companies** (the one noyau that maps to bleu-nuit).
 */
export const NOYAU_COLOR_HEX: Record<NoyauColorToken, { cssVar: string; hex: string }> = {
  'bleu-nuit': { cssVar: '--color-bleu-nuit-true', hex: '#312783' },
  'bleu-ciel': { cssVar: '--color-bleu-ciel', hex: '#009fe3' },
  vert: { cssVar: '--color-vert', hex: '#009640' },
  jaune: { cssVar: '--color-jaune', hex: '#efd500' },
  rouge: { cssVar: '--color-rouge', hex: '#e30613' },
  rose: { cssVar: '--color-rose', hex: '#e6007e' },
};

/** The ramp's non-noyau colours — its white end, its off-ramp grey, and the two text colours. */
export const RAMP_NEUTRAL_HEX = {
  blanc: { cssVar: '--color-blanc', hex: '#ffffff' },
  noir: { cssVar: '--color-noir', hex: '#000000' },
  sabotage: { cssVar: '--color-gray-200', hex: '#d9d6d2' },
} as const;

/**
 * First palier at which the header text flips from noir to blanc, per noyau —
 * `PALIER_RAMP_MAX + 1` means "never within the ramp".
 *
 * Derived from measured WCAG 2.1 contrast of noir against each ramp step
 * (sRGB mixing, 4.5:1 threshold for the palier label, which is normal-size
 * text). Only bleu-nuit and rouge ever get dark enough to need the flip:
 *
 *   bleu-nuit  p4 4.64 / p5 2.83 ✗ → blanc from p5 (7.43, 12.01)
 *   rouge      p5 4.92 / p6 4.30 ✗ → blanc from p6 (4.88)
 *   rose       p6 4.67 ✓ | vert p6 5.44 ✓ | bleu-ciel p6 7.07 ✓ | jaune p6 14.19 ✓
 *
 * Two margins are thin — bleu-nuit p4 (4.64) and rose p6 (4.67) — so do not
 * change the ramp spacing or the mixing colour space without re-running the
 * numbers. `noyau-gradient.test.ts` recomputes every pair and will fail if this
 * table drifts from the maths.
 */
export const NOYAU_TEXT_FLIP: Record<NoyauColorToken, number> = {
  'bleu-nuit': 5,
  rouge: 6,
  rose: PALIER_RAMP_MAX + 1,
  vert: PALIER_RAMP_MAX + 1,
  'bleu-ciel': PALIER_RAMP_MAX + 1,
  jaune: PALIER_RAMP_MAX + 1,
};

/**
 * One palier's position on the ramp — colour-syntax free.
 *
 * `sabotage` sits *outside* the ramp rather than below it, so it gets a neutral
 * grey instead of a seventh colour. `off-ramp` covers anything else (a
 * non-integer, or a palier number the ramp does not know), which renders white.
 */
export type PalierRampStep =
  | { kind: 'ramp'; token: NoyauColorToken; percent: number; textIsBlanc: boolean }
  | { kind: 'sabotage' }
  | { kind: 'off-ramp' };

/** Whether `palierNumber` sits on the 0..6 ramp. */
function isOnRamp(palierNumber: number): boolean {
  return Number.isInteger(palierNumber) && palierNumber >= 0 && palierNumber <= PALIER_RAMP_MAX;
}

/**
 * Where one palier header sits on the `noyau-gradient` ramp, for a company's
 * real `company.noyau` — never the `matrix-noyaux-accent` preview tweak, since
 * the header is a persistent identity cue rather than a swatch.
 */
export function palierRampStep(noyau: Noyau, palierNumber: number): PalierRampStep {
  if (!isOnRamp(palierNumber)) {
    return palierNumber === SABOTAGE_PALIER ? { kind: 'sabotage' } : { kind: 'off-ramp' };
  }
  const token = NOYAU_COLOR_TOKEN[noyau];
  return {
    kind: 'ramp',
    token,
    percent: (palierNumber / PALIER_RAMP_MAX) * 100,
    textIsBlanc: palierNumber >= NOYAU_TEXT_FLIP[token],
  };
}

/**
 * Per selected noyau, the ordered noyau code for each of the 6 "setup de
 * singularité" steps (étape 1..6) — a company's chosen noyau colors its own
 * setup table one row at a time, per this fixed table.
 */
export const NOYAU_STEP_ORDER: Record<Noyau, readonly Noyau[]> = {
  GSP: ['GPS', 'PGS', 'SGP', 'SPG', 'PSG', 'GSP'],
  GPS: ['GSP', 'SGP', 'PGS', 'PSG', 'SPG', 'GPS'],
  PGS: ['PSG', 'SPG', 'GPS', 'GSP', 'SGP', 'PGS'],
  PSG: ['PGS', 'GPS', 'SPG', 'SGP', 'GSP', 'PSG'],
  SPG: ['SGP', 'GSP', 'PSG', 'PGS', 'GPS', 'SPG'],
  SGP: ['SPG', 'PSG', 'GSP', 'GPS', 'PGS', 'SGP'],
};
